import React from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { isPngFile } from '../vibeCodingUtils';

const copyText = async (text) => {
  if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch (error) {
      // Fall through to the textarea fallback when the async API is blocked.
    }
  }

  const area = document.createElement('textarea');
  area.value = text;
  area.setAttribute('readonly', '');
  area.style.position = 'fixed';
  area.style.top = '0';
  area.style.left = '-9999px';
  document.body.appendChild(area);
  try {
    area.focus();
    area.select();
    const copied = document.execCommand('copy');
    if (!copied) throw new Error('copy failed');
  } finally {
    document.body.removeChild(area);
  }
};

const UnavailableButton = ({ label, iconClass }) => (
  <span className="vibe-toolbar-tip" title={label}>
    <button type="button" className="vibe-toolbar-icon" disabled aria-label={label}>
      <i className={`wb ${iconClass}`} aria-hidden="true" />
    </button>
  </span>
);

UnavailableButton.propTypes = {
  label: PropTypes.string.isRequired,
  iconClass: PropTypes.string.isRequired,
};

const EditorFileActions = ({ editorValue, selectedPath, isFileLoading }) => {
  const isPng = isPngFile(selectedPath);
  const canCopy = Boolean(selectedPath) && !isFileLoading && !isPng;
  const copyTitle = !selectedPath
    ? 'No file open'
    : isFileLoading
    ? 'File is still loading'
    : isPng
    ? 'Image files do not have text content'
    : 'Copy file content';

  const handleCopy = async () => {
    if (!canCopy) return;
    const text = editorValue == null ? '' : String(editorValue);
    try {
      await copyText(text);
      toast.success('File content copied');
    } catch (error) {
      toast.error('Unable to copy file content');
    }
  };

  return (
    <div className="vibe-editor-actions" role="group" aria-label="File actions">
      <UnavailableButton
        label="Add file reference (coming soon)"
        iconClass="webconnect-file-earmark-arrow-up"
      />
      <span className="vibe-toolbar-tip" title={copyTitle}>
        <button
          type="button"
          className="vibe-toolbar-icon"
          aria-label={copyTitle}
          title={canCopy ? copyTitle : undefined}
          disabled={!canCopy}
          onClick={handleCopy}
        >
          <i className="wb webconnect-files" aria-hidden="true" />
        </button>
      </span>
      <UnavailableButton label="Download file (coming soon)" iconClass="webconnect-download" />
    </div>
  );
};

EditorFileActions.propTypes = {
  editorValue: PropTypes.string,
  selectedPath: PropTypes.string,
  isFileLoading: PropTypes.bool,
};

EditorFileActions.defaultProps = {
  editorValue: '',
  selectedPath: '',
  isFileLoading: false,
};

export default EditorFileActions;
